import React, { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import toast from 'react-hot-toast'

import styles from './EditGuide.module.css'

import Input from 'Design/components/Input'
import Label from 'Design/components/Label'
import SwitchInput from 'Design/components/SwitchInput'
import FormErrorText from 'Design/components/FormErrorText'

import PageLoader from 'Layout/PageLoader'

import Tags from '../../../../../components/Tags'

import { useGetGuideByIdQuery, useUpdateGuideMutation } from '../guides.service'
import GuideLayout, { GuideMain, GuideSidebar, GuideTitle } from '../GuideLayout'
import GuideStep from './GuideSteps'
import parseErrorMessage from 'Utils/error-message-parser'
import ConfirmationModal from 'Design/components/ConfirmationModal'
import { hasHtmlContent } from 'Utils/html'

const EditGuideHandler = (props) => {
  const { guide } = props

  const [guideTitle, setGuideTitle] = useState(guide.name)
  const [isGuideActive, setIsGuideActive] = useState(guide.is_active)
  const [showUnPublishedModal, setShowUnPublishedModal] = useState(false)
  const [guideTitleError, setGuideTitleError] = useState(null)
  const [tags, setTags] = useState(guide.tags ?? [])
  const [updateGuide, { isLoading: isUpdating }] = useUpdateGuideMutation()

  const tagsValue = useMemo(() => {
    return tags.map((tag) => {
      return {
        label: tag,
        value: tag
      }
    })
  }, [tags])

  const update = async (payload) => {
    const promise = updateGuide({ id: guide.id, ...payload }).unwrap()

    toast.promise(promise, {
      loading: 'Updating guide',
      success: 'Guide updated successfully',
      error: parseErrorMessage('Unable to update guide. Try again...')
    })

    return promise
  }

  const checkErrorInTitle = (value) => {
    if (value.length < 3) {
      setGuideTitleError('Title must contain atleast 3 characters')
      return true
    }

    if (hasHtmlContent(value)) {
      setGuideTitleError('Title should not contain any HTML tags')
      return true
    }

    setGuideTitleError(null)
    return false
  }

  const handleTitleChange = (event) => {
    const { value } = event.target
    setGuideTitle(value)

    checkErrorInTitle(value)
  }

  const handleTitleBlur = (event) => {
    const { value } = event.target

    const hasError = checkErrorInTitle(value)
    if (hasError) return

    update({ name: value }).catch(() => {
      setGuideTitle(guide.name)
    })
  }

  const handleActiveGuideStateChange = (e) => {
    const isActive = e.target.checked

    if (isActive) {
      setIsGuideActive(isActive)
      update({ is_active: isActive }).catch(() => setIsGuideActive(!isActive))
    } else {
      setShowUnPublishedModal(true)
    }
  }

  const handlePublishedStateChange = (e) => {
    setIsGuideActive(false)
    update({ is_active: false }).catch(() => setIsGuideActive(true))
    setShowUnPublishedModal(false)
  }

  const handleTagCreate = (tag) => {
    setTags((previousTags) => {
      const newTags = [...previousTags, tag.value]
      update({ tags: newTags })
      return newTags
    })
  }

  const handleTagRemove = (tag) => {
    setTags((previousTags) => {
      const newTags = previousTags.filter((t) => t !== tag.value)
      update({ tags: newTags })
      return newTags
    })
  }

  const handleCloseUnPublishedModal = () => {
    setShowUnPublishedModal(false)
  }

  return (
    <GuideLayout>
      <ConfirmationModal
        isOpen={showUnPublishedModal}
        title='Unpublish Guide'
        onCloseButtonClick={handleCloseUnPublishedModal}
        onConfirmButtonClick={handlePublishedStateChange}
        confirmButtonText='Yes'
        cancelButtonText='No'
        disableConfirmButton={isUpdating}
      >
        Are you sure you want to unpublish the guide <strong>{guide?.name}</strong>? <br />
        This action will remove the guide from all connected steps.
      </ConfirmationModal>

      <GuideMain>
        <GuideTitle className={styles.GuideTitle}>
          <Label>Guide Title</Label>
          <Input value={guideTitle} onChange={handleTitleChange} onBlur={handleTitleBlur} />
          {guideTitleError && <FormErrorText>{guideTitleError}</FormErrorText>}
        </GuideTitle>
        <GuideStep guide={guide} />
      </GuideMain>
      <GuideSidebar>
        <SwitchInput checked={isGuideActive} onChange={handleActiveGuideStateChange}>
          Published
        </SwitchInput>
        <Tags
          value={tagsValue}
          creatable
          onCreate={handleTagCreate}
          onRemove={handleTagRemove}
          isDisabled={isUpdating}
          isLoading={isUpdating}
        />
      </GuideSidebar>
    </GuideLayout>
  )
}

const EditGuide = () => {
  const { id } = useParams()
  const { isLoading, data: guide } = useGetGuideByIdQuery(id)

  if (isLoading) {
    return <PageLoader />
  }

  return <EditGuideHandler guide={guide} />
}

export default EditGuide
